import type { CSSProperties, ReactNode, TransitionEvent } from "react";
import { use, createContext, useMemo } from "react";

import { useBannerRevealAnimation } from "app/components/PageTopBanner/useBannerRevealAnimation";

/**
 * Marks an element as the colored strip of a page-top banner, so the
 * PageTopBannerHost rules in `PageTopBanner` can animate its
 * own height (background) and fade its direct children in once the
 * height transition completes.
 */
export const PT_STRIP_ATTR = "data-pt-strip";

/**
 * Present on the strip while content should be fully visible. Toggled
 * automatically by usePageTopBannerStripReveal.
 */
export const PT_STRIP_CONTENT_VISIBLE_ATTR = "data-pt-strip-content-visible";

interface IPageTopBannerRevealValue {
    animatedHeight: number;
    isContentVisible: boolean;
    onTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => void;
}

const PageTopBannerRevealContext = createContext<IPageTopBannerRevealValue | null>(null);

interface IPageTopBannerRevealProviderProps {
    targetHeight: number;
    children: ReactNode;
}

/**
 * Single source of reveal animation state for `PageTopBanner`. Wraps the
 * chosen banner so descendant strips can opt-in via usePageTopBannerStripReveal.
 */
export const PageTopBannerRevealProvider = ({ targetHeight, children }: IPageTopBannerRevealProviderProps) => {
    const { animatedHeight, isContentVisible, onContainerTransitionEnd } = useBannerRevealAnimation({
        targetHeight,
    });

    const value = useMemo<IPageTopBannerRevealValue>(
        () => ({ animatedHeight, isContentVisible, onTransitionEnd: onContainerTransitionEnd }),
        [animatedHeight, isContentVisible, onContainerTransitionEnd],
    );

    return <PageTopBannerRevealContext.Provider value={value}>{children}</PageTopBannerRevealContext.Provider>;
};

interface IBannerStripRevealProps {
    style?: CSSProperties;
    onTransitionEnd?: (event: TransitionEvent<HTMLDivElement>) => void;
    [PT_STRIP_ATTR]?: string;
    [PT_STRIP_CONTENT_VISIBLE_ATTR]?: string;
}

/**
 * Spread the result onto the colored strip root of a banner inside
 * `PageTopBanner`. Outside of `PageTopBanner` the hook returns empty props and
 * the strip renders statically.
 */
export const usePageTopBannerStripReveal = (): IBannerStripRevealProps => {
    const ctx = use(PageTopBannerRevealContext);

    return useMemo<IBannerStripRevealProps>(() => {
        if (!ctx) {
            return {};
        }

        return {
            style: { height: `${ctx.animatedHeight}px` },
            onTransitionEnd: ctx.onTransitionEnd,
            [PT_STRIP_ATTR]: "",
            [PT_STRIP_CONTENT_VISIBLE_ATTR]: ctx.isContentVisible ? "" : undefined,
        };
    }, [ctx]);
};
